// การ์ด insight 1 เรื่อง — ใช้ทั้งแถบหน้าแรก (โต้ตอบได้) และ Insight Console (อ่านอย่างเดียว)
// 👍/👎 + ชิปเหตุผล (ไม่บังคับ), ทำแล้ว, ข้าม + หมายเหตุ, ดูรายการ, ดาวน์โหลด — ทุกอย่างคลิกเดียว
import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Paper,
  Radio, RadioGroup, FormControlLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Swal from 'sweetalert2';
import api from '../../adapters/api';

export const FEEDBACK_REASONS = {
  useful: 'มีประโยชน์ ได้ใช้จริง',
  known: 'รู้อยู่แล้ว',
  not_mine: 'ไม่เกี่ยวกับงานฉัน',
  wrong_numbers: 'ตัวเลขดูไม่ถูก',
  cant_act: 'ทำอะไรต่อไม่ได้',
};
const DOWN_REASONS = ['known', 'not_mine', 'wrong_numbers', 'cant_act'];
export const SKIP_REASONS = {
  not_relevant: 'ไม่เกี่ยวข้องกับงานวันนี้',
  already_done: 'จัดการไปแล้ว',
  reserved: 'ตั้งใจจองไว้',
  other: 'อื่น ๆ',
};

// ดาวน์โหลดผ่าน axios (แนบ cookie session) แล้วให้เบราว์เซอร์บันทึกไฟล์
export async function downloadAttachment(url, fallbackName = 'insight.xlsx') {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const cd = res.headers['content-disposition'] || '';
    const m = cd.match(/filename\*=UTF-8''([^;]+)/);
    const name = m ? decodeURIComponent(m[1]) : fallbackName;
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (err) {
    let msg = err.message;
    try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* ไม่ใช่ JSON */ }
    Swal.fire({ icon: 'error', title: 'ดาวน์โหลดไม่สำเร็จ', text: msg, confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
  }
}

export function SkipDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('not_relevant');
  const [note, setNote] = useState('');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>ข้ามเรื่องนี้</DialogTitle>
      <DialogContent>
        <RadioGroup value={reason} onChange={e => setReason(e.target.value)}>
          {Object.entries(SKIP_REASONS).filter(([k]) => k !== 'reserved').map(([k, label]) => ( // "ตั้งใจจองไว้" กดรายใบในแผงดูรายการ
            <FormControlLabel key={k} value={k} control={<Radio size="small" />} label={label} />
          ))}
        </RadioGroup>
        <TextField
          fullWidth size="small" margin="dense" label="หมายเหตุ (ไม่บังคับ)" value={note}
          onChange={e => setNote(e.target.value)} inputProps={{ maxLength: 500 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#757575' }}>ยกเลิก</Button>
        <Button variant="contained" onClick={() => onConfirm(reason, note)}>ข้าม</Button>
      </DialogActions>
    </Dialog>
  );
}

const PREVIEW_TIP = 'โหมดดูในฐานะผู้ใช้ — อ่านอย่างเดียว (ผู้ใช้จริงกดปุ่มนี้ได้)';

// actions = { feedback(value, reason), status(status, extra), openRows(), download(), preview? } — ไม่มี feedback = อ่านอย่างเดียว
export default function InsightCard({ task, actions, extra, minWidth = 290 }) {
  const [anchor, setAnchor] = useState(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const f = task.facts || {};
  const readOnly = !actions?.feedback; // console ส่งมาแค่ openRows/download
  const preview = Boolean(actions?.preview); // โหมดดูในฐานะผู้ใช้: แสดงปุ่มครบเหมือนผู้ใช้จริงแต่กดไม่ได้
  const done = task.status === 'done';
  const skipped = task.status === 'skipped';

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5, py: 1, minWidth, maxWidth: 420, flex: `1 0 ${minWidth}px`, borderRadius: 2,
        display: 'flex', flexDirection: 'column', gap: 0.5, scrollSnapAlign: 'start',
        opacity: done || skipped ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Typography variant="body2" fontWeight={700} lineHeight={1.35} sx={{ flex: 1, minWidth: 0 }}>{task.title}</Typography>
        {(!readOnly || preview) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mt: -0.5, mr: -0.75 }}>
            <Tooltip title={preview ? PREVIEW_TIP : done ? 'ยกเลิก "ทำแล้ว"' : 'ทำแล้ว'}>
              <span>
                <IconButton size="small" disabled={readOnly} onClick={() => actions.status(done ? 'open' : 'done')}>
                  {done ? <CheckCircleIcon fontSize="small" color="success" /> : <CheckCircleOutlineIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={preview ? PREVIEW_TIP : skipped ? 'ยกเลิกการข้าม' : 'ข้ามเรื่องนี้'}>
              <span>
                <Button
                  size="small" disabled={readOnly} sx={{ color: 'text.secondary', minWidth: 0, px: 0.75 }}
                  onClick={() => (skipped ? actions.status('open') : setSkipOpen(true))}
                >
                  {skipped ? 'ยกเลิกข้าม' : 'ข้าม'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
      {(f.lines || []).map((line, i) => (
        <Typography key={i} variant="caption" color="text.secondary" lineHeight={1.4}>{line}</Typography>
      ))}
      {f.accuracy && (
        <Typography variant="caption" sx={{ color: 'primary.main' }} lineHeight={1.4}>{f.accuracy}</Typography>
      )}
      {extra}
      {skipped && (
        <Typography variant="caption" color="text.secondary">
          ข้ามแล้ว{task.skipReason ? `: ${SKIP_REASONS[task.skipReason] || task.skipReason}` : ''}{task.note ? ` — ${task.note}` : ''}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 'auto', flexWrap: 'wrap' }}>
        <Tooltip title={preview ? PREVIEW_TIP : 'มีประโยชน์'}>
          <span>
            <IconButton size="small" disabled={readOnly} onClick={() => actions.feedback(task.feedback === 1 ? 0 : 1, task.feedback === 1 ? null : 'useful')}>
              {task.feedback === 1 ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={preview ? PREVIEW_TIP : 'ไม่มีประโยชน์'}>
          <span>
            <IconButton size="small" disabled={readOnly} onClick={e => (task.feedback === -1 ? actions.feedback(0, null) : setAnchor(e.currentTarget))}>
              {task.feedback === -1 ? <ThumbDownIcon fontSize="small" color="error" /> : <ThumbDownOutlinedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {task.feedbackReason && task.feedback === -1 && (
          <Chip size="small" variant="outlined" label={FEEDBACK_REASONS[task.feedbackReason] || task.feedbackReason} />
        )}
        <Box sx={{ flex: 1 }} />
        {task.attachment && (
          <>
            <Button size="small" onClick={() => actions?.openRows ? actions.openRows() : null} disabled={!actions?.openRows}>
              ดูรายการ ({task.attachment.rows}) ›
            </Button>
            <Tooltip title="ดาวน์โหลด Excel">
              <span>
                <IconButton size="small" disabled={!actions?.download} onClick={() => actions.download()}>
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem disabled dense><Typography variant="caption">เพราะอะไร? (ไม่บังคับ)</Typography></MenuItem>
        {DOWN_REASONS.map(k => (
          <MenuItem key={k} dense onClick={() => { setAnchor(null); actions.feedback(-1, k); }}>{FEEDBACK_REASONS[k]}</MenuItem>
        ))}
        <MenuItem dense onClick={() => { setAnchor(null); actions.feedback(-1, null); }}>ไม่ระบุ</MenuItem>
      </Menu>
      {!readOnly && (
        <SkipDialog
          open={skipOpen}
          onClose={() => setSkipOpen(false)}
          onConfirm={(reason, note) => { setSkipOpen(false); actions.status('skipped', { skipReason: reason, note }); }}
        />
      )}
    </Paper>
  );
}
